import { useMemo, useState } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	Chip,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	Typography
} from '@mui/material';
import type { DongCapNhat, KetQuaPhanLoai } from '@/services/importApi';

const SO_DONG_MOI_TRANG = 25;

export interface ImportUpdateConfirmDialogProps {
	open: boolean;
	/** Nhãn loại dữ liệu đang nhập, để tiêu đề nói rõ đang xác nhận cái gì. */
	tenLoai: string;
	ketQua: KetQuaPhanLoai | null;
	dangGhi?: boolean;
	onHuy: () => void;
	/** Nhận danh sách khoá được tick; mảng rỗng nghĩa là không ghi đè gì. */
	onXacNhan: (khoaDuocChon: string[]) => void;
}

/**
 * Màn xác nhận trước khi ghi đè dữ liệu đang có — dùng chung cho mọi luồng nhập.
 *
 * Component không biết mình đang nhập tỉnh, trường, phương thức hay sinh viên:
 * mọi thứ hiển thị đều đến từ payload phân loại của server. Thêm một luồng nữa
 * chỉ cần khai ở phía server, không phải đụng vào đây.
 *
 * Mặc định KHÔNG tick dòng nào. Ghi đè là thao tác mất dữ liệu, nên phải là một
 * lựa chọn chủ động chứ không phải thứ xảy ra khi người dùng bấm nhanh qua.
 */
export default function ImportUpdateConfirmDialog({
	open,
	tenLoai,
	ketQua,
	dangGhi = false,
	onHuy,
	onXacNhan
}: ImportUpdateConfirmDialogProps) {
	const [khoaDaChon, setKhoaDaChon] = useState<Set<string>>(new Set());
	const [trang, setTrang] = useState(0);

	const dongCapNhat = useMemo(() => ketQua?.dongCapNhat ?? [], [ketQua]);
	const tongCanHoi = dongCapNhat.length;
	const soDaChon = khoaDaChon.size;

	const dongTrongTrang = useMemo(
		() => dongCapNhat.slice(trang * SO_DONG_MOI_TRANG, (trang + 1) * SO_DONG_MOI_TRANG),
		[dongCapNhat, trang]
	);

	const doiMotDong = (khoa: string) => {
		setKhoaDaChon((truoc) => {
			const sau = new Set(truoc);
			if (sau.has(khoa)) sau.delete(khoa);
			else sau.add(khoa);
			return sau;
		});
	};

	const doiTatCa = () => {
		setKhoaDaChon((truoc) =>
			truoc.size === tongCanHoi ? new Set() : new Set(dongCapNhat.map((d) => d.khoa))
		);
	};

	const dong = (d: DongCapNhat) => (
		<TableRow key={d.khoa} hover selected={khoaDaChon.has(d.khoa)}>
			<TableCell padding="checkbox">
				<Checkbox
					checked={khoaDaChon.has(d.khoa)}
					onChange={() => doiMotDong(d.khoa)}
					inputProps={{ 'aria-label': `Cập nhật ${d.khoa}` }}
				/>
			</TableCell>
			<TableCell sx={{ whiteSpace: 'nowrap', fontFamily: 'monospace' }}>{d.khoa}</TableCell>
			<TableCell>{d.moTa ?? '—'}</TableCell>
			<TableCell>
				<Stack spacing={0.5}>
					{d.thayDoi.map((t) => (
						<Typography key={t.truong} variant="body2" component="div">
							<Box component="span" sx={{ color: 'text.secondary' }}>
								{t.nhan}:{' '}
							</Box>
							<Box component="span" sx={{ textDecoration: 'line-through', color: 'text.disabled' }}>
								{t.cu ?? '(trống)'}
							</Box>
							<Box component="span" sx={{ mx: 0.5 }}>
								→
							</Box>
							<Box component="span" sx={{ fontWeight: 600 }}>
								{t.moi ?? '(trống)'}
							</Box>
						</Typography>
					))}
				</Stack>
			</TableCell>
		</TableRow>
	);

	return (
		<Dialog open={open} onClose={dangGhi ? undefined : onHuy} maxWidth="lg" fullWidth>
			<DialogTitle>Xác nhận cập nhật — {tenLoai}</DialogTitle>
			<DialogContent dividers>
				<Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
					<Chip color="success" label={`Thêm mới: ${ketQua?.soThemMoi ?? 0}`} />
					<Chip color="warning" label={`Sẽ ghi đè: ${tongCanHoi}`} />
					<Chip variant="outlined" label={`Đang chọn: ${soDaChon}`} />
				</Stack>

				{ketQua?.biCatBot && (
					<Alert severity="warning" sx={{ mb: 2 }}>
						Số dòng cập nhật vượt quá mức hiển thị được. Bảng dưới chỉ là một phần, nên lần
						nhập này sẽ ghi đè TOÀN BỘ như trước — hãy chia nhỏ file nếu muốn chọn từng dòng.
					</Alert>
				)}

				{tongCanHoi === 0 ? (
					<Alert severity="success">
						Không có bản ghi nào bị ghi đè. {ketQua?.soThemMoi ?? 0} dòng sẽ được thêm mới.
					</Alert>
				) : (
					<>
						<Alert severity="info" sx={{ mb: 2 }}>
							Chỉ những dòng được tick mới ghi đè dữ liệu đang có. Dòng không tick sẽ giữ
							nguyên bản ghi cũ. {ketQua?.soThemMoi ?? 0} dòng thêm mới luôn được nhập.
						</Alert>
						<TableContainer sx={{ maxHeight: 440 }}>
							<Table size="small" stickyHeader>
								<TableHead>
									<TableRow>
										<TableCell padding="checkbox">
											<Checkbox
												checked={tongCanHoi > 0 && soDaChon === tongCanHoi}
												indeterminate={soDaChon > 0 && soDaChon < tongCanHoi}
												onChange={doiTatCa}
												inputProps={{ 'aria-label': 'Chọn tất cả' }}
											/>
										</TableCell>
										<TableCell>Mã</TableCell>
										<TableCell>Bản ghi hiện có</TableCell>
										<TableCell>Sẽ thay đổi</TableCell>
									</TableRow>
								</TableHead>
								<TableBody>{dongTrongTrang.map(dong)}</TableBody>
							</Table>
						</TableContainer>
						<TablePagination
							component="div"
							count={tongCanHoi}
							page={trang}
							onPageChange={(_, p) => setTrang(p)}
							rowsPerPage={SO_DONG_MOI_TRANG}
							rowsPerPageOptions={[SO_DONG_MOI_TRANG]}
							labelDisplayedRows={({ from, to, count }) => `${from}–${to} trên ${count}`}
						/>
					</>
				)}
			</DialogContent>
			<DialogActions>
				<Button onClick={onHuy} disabled={dangGhi}>
					Huỷ
				</Button>
				<Button
					variant="contained"
					disabled={dangGhi}
					onClick={() => onXacNhan([...khoaDaChon])}
				>
					{soDaChon > 0 ? `Nhập và ghi đè ${soDaChon} bản ghi` : 'Nhập, không ghi đè'}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
